/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthProvider";
import { useApiStatus } from "../context/ApiStatusProvider";
import { api } from "../services/api";
import { Mail, MessageCircle } from "lucide-react";

// Interfaces e tipos
interface EmailConfirmationProps {
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onPostpone: () => void;
}

type ConfirmationMethod = "email" | "whatsapp" | null;

// Componente principal
const EmailConfirmation: React.FC<EmailConfirmationProps> = ({
  isVisible,
  onClose,
  onSuccess,
}) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [confirmationMethod, setConfirmationMethod] = useState<ConfirmationMethod>(null);
  const [userEmail, setUserEmail] = useState<string>("");
  const [userTelefone, setUserTelefone] = useState<string>("");
  const [requestId, setRequestId] = useState<string>("");

  // Estados de loading
  const [isLoading, setIsLoading] = useState(false);

  const { user, login } = useAuth();
  const { emailValidationEnabled, whatsappValidationEnabled } = useApiStatus();

  // Estados para os inputs totalmente separados
  const [emailValue, setEmailValue] = useState("");
  const [telefoneValue, setTelefoneValue] = useState("");
  const [codigoValue, setCodigoValue] = useState("");
  const [emailError, setEmailError] = useState("");
  const [telefoneError, setTelefoneError] = useState("");
  const [codigoError, setCodigoError] = useState("");

  // Estados para o timer de reenvio
  const [timerRemaining, setTimerRemaining] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [resendAttempts, setResendAttempts] = useState(0);

  // Reset do formulário
  const resetForm = useCallback(() => {
    setCurrentStep(0);
    setConfirmationMethod(null);
    setCodigoValue("");
    setEmailValue(user?.email || "");
    setTelefoneValue(user?.telefone || "");
    setEmailError("");
    setTelefoneError("");
    setCodigoError("");
    setRequestId("");
    setTimerRemaining(0);
    setTimerActive(false);
    setResendAttempts(0);
  }, [user?.email, user?.telefone]);

  // Inicializar com os dados do usuário, se disponível
  useEffect(() => {
    if (user?.email) {
      setUserEmail(user.email);
      setEmailValue(user.email);
    }
    if (user?.telefone) {
      setUserTelefone(user.telefone);
      setTelefoneValue(user.telefone);
    }
  }, [user]);

  // Controle de visibilidade do modal
  useEffect(() => {
    if (!isVisible) {
      resetForm();
    }
  }, [isVisible, resetForm]);

  // Controle do timer de reenvio
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (timerActive && timerRemaining > 0) {
      interval = setInterval(() => {
        setTimerRemaining((prevCount) => {
          if (prevCount <= 1) {
            setTimerActive(false);
            return 0;
          }
          return prevCount - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [timerActive, timerRemaining]);

  // Função para formatar telefone
  const formatTelefone = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3");
  };

  // Função para validar telefone
  const validateTelefone = (telefone: string): boolean => {
    const numbers = telefone.replace(/\D/g, "");
    return numbers.length === 10 || numbers.length === 11;
  };

  // Funções de navegação entre passos
  const handleConfirmNow = () => {
    setCurrentStep(1);
  };

  const handleSelectMethod = (method: "email" | "whatsapp") => {
    setConfirmationMethod(method);
    setCurrentStep(2);
  };

  // Função para enviar código por email
  const handleSendEmailCode = async () => {
    // Validar email
    if (!emailValue || !emailValue.match(/^\S+@\S+\.\S+$/)) {
      setEmailError("Email inválido");
      return;
    }

    setIsLoading(true);
    setEmailError("");

    try {
      const response = await api.post("/login/confirm-email/send-email", {
        email: emailValue,
      });

      const newRequestId = response.data.id;
      setRequestId(newRequestId);
      setUserEmail(emailValue);
      setCodigoValue("");
      setTimerRemaining(60);
      setTimerActive(true);
      setResendAttempts(1);
      setCurrentStep(3);
    } catch (error: any) {
      setEmailError(error.response?.data?.error || "Não foi possível enviar o código.");
    } finally {
      setIsLoading(false);
    }
  };

  // Função para enviar código por WhatsApp
  const handleSendWhatsAppCode = async () => {
    // Validar telefone
    if (!validateTelefone(telefoneValue)) {
      setTelefoneError("Telefone inválido. Use o formato (XX) XXXXX-XXXX");
      return;
    }

    setIsLoading(true);
    setTelefoneError("");

    try {
      const telefoneNumbers = telefoneValue.replace(/\D/g, "");
      const response = await api.post("/login/confirm-whatsapp/send-whatsapp", {
        whatsapp: telefoneNumbers,
      });

      const newRequestId = response.data.id;
      setRequestId(newRequestId);
      setUserTelefone(telefoneValue);
      setCodigoValue("");
      setTimerRemaining(60);
      setTimerActive(true);
      setResendAttempts(1);
      setCurrentStep(3);
    } catch (error: any) {
      setTelefoneError(error.response?.data?.error || "Não foi possível enviar o código.");
    } finally {
      setIsLoading(false);
    }
  };

  // Função para verificar código
  const handleVerificarCodigo = async () => {
    // Validar código
    if (
      !codigoValue ||
      codigoValue.length !== 6 ||
      !/^\d+$/.test(codigoValue)
    ) {
      setCodigoError("Digite um código válido de 6 dígitos");
      return;
    }

    setIsLoading(true);
    setCodigoError("");

    try {
      const endpoint = confirmationMethod === "email"
        ? "/login/confirm-email/verify-code"
        : "/login/confirm-whatsapp/verify-code";

      const response = await api.post(endpoint, {
        id: requestId,
        code: codigoValue,
      });
      
      if (response.status !== 200) {
        throw new Error("Código inválido");
      }

      // Atualizar o usuário no contexto
      if (user) {
        const updatedUser = {
          ...user,
          ...(confirmationMethod === "email" ? { isEmailVerified: true } : { isWhatsappVerified: true }),
        };
        login(updatedUser);
      }

      resetForm();
      setCurrentStep(4);
    } catch (error: any) {
      setCodigoError(error.response?.data?.error || "Código inválido ou expirado.");
    } finally {
      setIsLoading(false);
    }
  };

  // Função para reenviar o código
  const handleReenviarCodigo = async () => {
    setIsLoading(true);

    try {
      if (confirmationMethod === "email") {
        const response = await api.post("/login/confirm-email/send-email", {
          email: userEmail,
        });
        setRequestId(response.data.id);
      } else {
        const telefoneNumbers = userTelefone.replace(/\D/g, "");
        const response = await api.post("/login/confirm-whatsapp/send-whatsapp", {
          whatsapp: telefoneNumbers,
        });
        setRequestId(response.data.id);
      }

      // Calcular novo tempo do timer: 60 segundos + (tentativas * 30 segundos)
      const newTimerTime = 60 + (resendAttempts * 30);
      setTimerRemaining(newTimerTime);
      setTimerActive(true);
      setResendAttempts(prev => prev + 1);

      const destino = confirmationMethod === "email" ? userEmail : userTelefone;
      alert(`Um novo código foi enviado para ${destino}`);
    } catch (error: any) {
      alert("Não foi possível reenviar o código.");
    } finally {
      setIsLoading(false);
    }
  };

  // Função para formatar o tempo do timer
  const formatTimer = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Componentes de UI
  const renderStepIndicator = () => (
    <div className="flex justify-center mb-6">
      {[0, 1, 2, 3, 4].map((step) => (
        <div key={step} className="flex items-center">
          <div
            className={`w-8 h-8 rounded-full ${
              currentStep >= step ? "bg-blue-500" : "bg-gray-300"
            } flex items-center justify-center`}
          >
            <span className="text-white font-bold text-sm">{step + 1}</span>
          </div>
          {step < 4 && (
            <div
              className={`h-1 w-8 ${
                currentStep > step ? "bg-blue-500" : "bg-gray-300"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderProgressBar = () => (
    <div className="mb-6">
      <div className="flex justify-between mb-2">
        <span className="text-sm text-gray-500 font-medium">
          Passo {currentStep + 1} de 5
        </span>
        <span className="text-sm text-blue-500 font-medium">
          {Math.round(((currentStep + 1) / 5) * 100)}%
        </span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-500 rounded-full transition-all duration-300"
          style={{ width: `${((currentStep + 1) / 5) * 100}%` }}
        />
      </div>
    </div>
  );

  const renderActionButton = (
    title: string,
    onPress: () => void,
    isLoadingBtn: boolean = false,
    variant: "primary" | "secondary" = "primary",
  ) => (
    <button
      className={`w-full rounded-full h-16 flex items-center justify-center transition-all duration-200 hover:opacity-90 active:scale-95 disabled:opacity-70 ${
        variant === "primary"
          ? "bg-blue-600 text-white"
          : "bg-gray-200 text-gray-800"
      }`}
      onClick={onPress}
      disabled={isLoadingBtn}
    >
      {isLoadingBtn ? (
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-current"></div>
      ) : (
        <span className="text-center font-semibold text-lg">
          {title}
        </span>
      )}
    </button>
  );

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-center text-blue-600 mb-4">
            Confirmação de Conta
          </h2>

          {renderStepIndicator()}
          {renderProgressBar()}

          {currentStep === 0 ? (
            <div className="bg-white p-6 rounded-3xl">
              <h3 className="text-xl font-semibold mb-2 text-gray-800">
                Deseja confirmar sua conta?
              </h3>
              <p className="text-gray-500 mb-6">
                É importante confirmar sua conta para garantir a segurança e receber notificações importantes.
              </p>

              <div className="mt-6 space-y-4">
                {renderActionButton("Confirmar agora", handleConfirmNow)}
              </div>
            </div>
          ) : currentStep === 1 ? (
            <div className="bg-white p-6 rounded-3xl">
              <h3 className="text-xl font-semibold mb-2 text-gray-800">
                Escolha o método de confirmação
              </h3>
              <p className="text-gray-500 mb-6">
                Selecione como deseja receber o código de verificação:
              </p>

              <div className="space-y-3">
                {emailValidationEnabled && (
                  <button
                    onClick={() => handleSelectMethod("email")}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all flex items-center gap-4"
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Mail className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1 text-left">
                      <h4 className="font-semibold text-gray-800">Confirmar por Email</h4>
                      <p className="text-sm text-gray-500">Receba o código no seu email</p>
                    </div>
                  </button>
                )}

                {whatsappValidationEnabled && (
                  <button
                    onClick={() => handleSelectMethod("whatsapp")}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all flex items-center gap-4"
                  >
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <MessageCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="flex-1 text-left">
                      <h4 className="font-semibold text-gray-800">Confirmar por WhatsApp</h4>
                      <p className="text-sm text-gray-500">Receba o código no seu WhatsApp</p>
                    </div>
                  </button>
                )}

                {!emailValidationEnabled && !whatsappValidationEnabled && (
                  <p className="text-center text-gray-500 py-4">
                    Nenhum método de confirmação disponível no momento.
                  </p>
                )}
              </div>

              <div className="mt-6">
                {renderActionButton(
                  "Voltar",
                  () => setCurrentStep(0),
                  false,
                  "secondary"
                )}
              </div>
            </div>
          ) : currentStep === 2 ? (
            <div className="bg-white p-6 rounded-3xl">
              <h3 className="text-xl font-semibold mb-2 text-gray-800">
                {confirmationMethod === "email" ? "Confirme seu email" : "Confirme seu WhatsApp"}
              </h3>
              <p className="text-gray-500 mb-6">
                Verifique se os dados abaixo estão corretos ou edite se necessário:
              </p>

              {confirmationMethod === "email" ? (
                <div className="mb-4">
                  <div
                    className={`flex items-center w-full bg-white rounded-full h-16 px-4 border ${
                      emailError ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <Mail className="h-5 w-5 text-gray-400 mr-2" />
                    <input
                      className="flex-1 text-gray-800 text-base outline-none"
                      placeholder="Seu email"
                      onChange={(e) => {
                        setEmailValue(e.target.value);
                        setEmailError("");
                      }}
                      value={emailValue}
                      type="email"
                      autoComplete="email"
                      autoCorrect="off"
                      autoCapitalize="none"
                    />
                  </div>
                  {emailError && (
                    <span className="text-red-500 text-xs ml-4 mt-1 block">
                      {emailError}
                    </span>
                  )}
                </div>
              ) : (
                <div className="mb-4">
                  <div
                    className={`flex items-center w-full bg-white rounded-full h-16 px-4 border ${
                      telefoneError ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <MessageCircle className="h-5 w-5 text-gray-400 mr-2" />
                    <input
                      className="flex-1 text-gray-800 text-base outline-none"
                      placeholder="(XX) XXXXX-XXXX"
                      onChange={(e) => {
                        const formatted = formatTelefone(e.target.value);
                        setTelefoneValue(formatted);
                        setTelefoneError("");
                      }}
                      value={telefoneValue}
                      type="tel"
                      maxLength={15}
                    />
                  </div>
                  {telefoneError && (
                    <span className="text-red-500 text-xs ml-4 mt-1 block">
                      {telefoneError}
                    </span>
                  )}
                </div>
              )}

              <div className="mt-6 space-y-4">
                {renderActionButton(
                  "Continuar",
                  confirmationMethod === "email" ? handleSendEmailCode : handleSendWhatsAppCode,
                  isLoading
                )}
                {renderActionButton(
                  "Voltar",
                  () => setCurrentStep(1),
                  false,
                  "secondary"
                )}
              </div>
            </div>
          ) : currentStep === 3 ? (
            <div className="bg-white p-6 rounded-3xl">
              <h3 className="text-xl font-semibold mb-2 text-gray-800">
                Verificação de {confirmationMethod === "email" ? "Email" : "WhatsApp"}
              </h3>
              <p className="text-gray-500 mb-6">
                Digite o código de 6 dígitos enviado para{" "}
                <strong>
                  {confirmationMethod === "email" ? userEmail : userTelefone}
                </strong>
              </p>

              <div className="mb-4">
                <div
                  className={`flex items-center w-full bg-white rounded-full h-16 px-4 border ${
                    codigoError ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <input
                    className="flex-1 text-gray-800 text-base outline-none text-center text-2xl tracking-widest"
                    placeholder="000000"
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/[^0-9]/g, "");
                      setCodigoValue(numericValue);
                      setCodigoError("");
                    }}
                    value={codigoValue}
                    type="text"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    maxLength={6}
                  />
                </div>
                {codigoError && (
                  <span className="text-red-500 text-xs ml-4 mt-1 block">
                    {codigoError}
                  </span>
                )}
              </div>

              <div className="mt-6">
                {renderActionButton(
                  "Verificar",
                  handleVerificarCodigo,
                  isLoading
                )}
              </div>

              <button
                className={`mt-4 w-full text-center ${
                  timerActive 
                    ? "text-gray-400 cursor-not-allowed" 
                    : "text-blue-500 hover:underline"
                }`}
                onClick={handleReenviarCodigo}
                disabled={isLoading || timerActive}
              >
                {isLoading
                  ? "Enviando..."
                  : timerActive
                  ? `Aguarde ${formatTimer(timerRemaining)} para reenviar`
                  : "Não recebeu o código? Reenviar"}
              </button>

              <button
                className="mt-2 w-full text-blue-500 text-center hover:underline"
                onClick={() => {
                  setCurrentStep(2);
                  setCodigoValue("");
                }}
              >
                Editar {confirmationMethod === "email" ? "email" : "telefone"}
              </button>
            </div>
          ) : (
            <div className="bg-white p-6 rounded-3xl text-center">
              <div className="w-20 h-20 rounded-full bg-green-500 flex items-center justify-center mb-4 mx-auto">
                <span className="text-white text-3xl">✓</span>
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-800">
                {confirmationMethod === "email" ? "Email Confirmado" : "WhatsApp Confirmado"}
              </h3>
              <p className="text-gray-500 mb-6">
                Sua conta foi verificada com sucesso!
              </p>

              <div className="mt-6 space-y-4">
                {renderActionButton(
                  "Continuar",
                  () => {
                    resetForm();
                    onSuccess();
                    onClose();
                  },
                  false
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailConfirmation;